'use client';

import { SanityImage } from '../types';
import { ComponentButton, type ComponentButtonProps } from '../Inputs/Button';
import { Card, type CardProps } from '../Card';
import { Media } from '../Media';
import { Stat, type StatisticProps } from '../Stat';
import { Text } from '../Text';
import { Video } from '../Video';
import { ReactNode } from 'react';
import { isValidRichText } from '../_lib/isValidRichText';

export type PanelProps = {
	autoplayVideo?: string;
	image?: SanityImage;
	label?: string;
	title?: string;
	meta?: string[];
	description?: {
		label?: string;
		copy?: ReactNode;
	};
	headerChildren?: ReactNode;
	children?: ReactNode;
	video?: string;
};

export function Panel(props: PanelProps) {
	return (
		<div className='flex flex-col gap-responsive-lg mt-4'>
			<div className='grid sm:grid-cols-2 gap-spacing-md'>
				{props.image && (
					<div className={`rounded-lg overflow-hidden aspect-4/5 sm:aspect-square`}>
						{props.autoplayVideo || props.video ? (
							<Video
								className='px-0 h-full [&>div]:h-full [&_img]:w-full [&_img]:h-full'
								autoplayVideo={props.autoplayVideo}
								image={props.image}
								video={props.video}
								buttonSize='sm'
								size='unset'
								nestedPopup={true}
							/>
						) : (
							<Media className='h-full [&_img]:w-full [&_img]:h-full' image={props.image} />
						)}
					</div>
				)}

				<div className='flex flex-col'>
					{props.label && (
						<Text className='text-text-brand mb-3' styleType='label-sm'>
							{props.label}
						</Text>
					)}

					{props.title && (
						<Text as='h2' className='text-text-primary mb-3' styleType='heading-xs'>
							{props.title}
						</Text>
					)}

					{props.meta && props.meta.length > 0 && (
						<ul className={`flex flex-wrap`}>
							{props.meta.map((item, index) => {
								return (
									<Text
										className={`py-1 text-text-secondary flex items-center last:after:hidden after:bg-current after:mx-1.5 after:h-[0.1875rem] after:w-[0.1875rem] after:rounded-full`}
										key={index}
										as='li'
										styleType='chip-md'
									>
										{item}
									</Text>
								);
							})}
						</ul>
					)}

					{props.headerChildren && <div className={`mt-auto pt-responsive-2xs`}>{props.headerChildren}</div>}
				</div>
			</div>

			<div className='h-px w-full bg-border-secondary' />

			{props.description && (
				<div>
					{props.description?.label && (
						<Text as='h3' className='mb-responsive-xs text-text-primary' styleType='label-md'>
							{props.description.label}
						</Text>
					)}

					{isValidRichText(props.description.copy) && (
						<Text className='text-text-secondary' styleType='body-md'>
							{props.description.copy}
						</Text>
					)}
				</div>
			)}

			{props.children}
		</div>
	);
}

export type PanelPersonProps = PanelProps & {
	cardTitle?: string;
	cards?: CardProps[];
	logo?: SanityImage;
};

export function PanelPerson(props: PanelPersonProps) {
	return (
		<Panel
			label={props.label}
			title={props.title}
			meta={props.meta}
			image={props.image}
			description={props.description}
			headerChildren={
				props.logo && (
					<div className={`max-w-20 w-fit`}>
						<Media className='h-full w-full [&_img]:w-auto! [&_img]:h-10' objectFit='contain' image={props.logo} />
					</div>
				)
			}
		>
			<div>
				{props.cardTitle && (
					<Text className='mb-responsive-xs text-text-primary' styleType='label-md'>
						{props.cardTitle}
					</Text>
				)}
				{props.cards && props.cards.length > 0 && (
					<div className='flex flex-col gap-responsive-xs'>
						{props.cards.map((item, i) => (
							<Card key={i} layout='horizontal' size='md' {...item} />
						))}
					</div>
				)}
			</div>
		</Panel>
	);
}

export type PanelStoryProps = PanelProps & {
	buttons?: ComponentButtonProps[];
	logo?: SanityImage;
	stats?: StatisticProps[];
};

export function PanelStory(props: PanelStoryProps) {
	return (
		<Panel
			label={props.label}
			title={props.title}
			meta={props.meta}
			image={props.image}
			description={props.description}
			autoplayVideo={props.autoplayVideo}
			video={props.video}
			headerChildren={
				props.logo && (
					<div className={`max-w-20 w-fit`}>
						<Media
							className='h-full w-full [&_img]:w-auto! [&_img]:h-10 [&_img]:brightness-[0] [&_img]:contrast-[1]'
							objectFit='contain'
							image={props.logo}
						/>
					</div>
				)
			}
		>
			<div className={`flex flex-col gap-responsive-lg`}>
				{props.stats && props.stats.length > 0 && (
					<div className='grid grid-cols-2 gap-responsive-md'>
						{props.stats.map((item, i) => (
							<Stat key={i} {...item} size='small' />
						))}
					</div>
				)}
				{props.buttons && props.buttons.length > 0 && (
					<div className={`flex flex-wrap gap-responsive-xs`}>
						{props.buttons.map((item, index) => (
							<ComponentButton key={index} {...item} />
						))}
					</div>
				)}
			</div>
		</Panel>
	);
}

export type PanelEventProps = PanelProps & {
	buttons?: ComponentButtonProps[];
};

export function PanelEvent(props: PanelEventProps) {
	return (
		<Panel
			label={props.label}
			title={props.title}
			meta={props.meta}
			image={props.image}
			description={props.description}
			autoplayVideo={props.autoplayVideo}
			video={props.video}
		>
			<div>
				{props.buttons && props.buttons.length > 0 && (
					<div className={`flex flex-wrap gap-responsive-xs`}>
						{props.buttons.map((item, index) => (
							<ComponentButton key={index} {...item} />
						))}
					</div>
				)}
			</div>
		</Panel>
	);
}
